import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Building2, Image as ImageIcon, ImageUp, Loader2, Type, X } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { compressCompanyLogo, COMPANY_LOGO_ACCEPT } from '../../../utils/compressCompanyLogo';
import { Ci2HelpHexIcon } from '../../process-config/components/Ci2HelpHexIcon';
import CxExpAnchorTooltip from '../../customer-experience/components/CxExpAnchorTooltip';
import '../../customer-experience/components/CxEmailTemplateModal.css';
import '../../customer-experience/components/CxEmailSendMailModal.css';
import './CustomersEmpresasModal.css';

const API_BASE = () => `${getApiBaseUrl()}/api/config/vc-cfg-company`;

function authHeaders(json = true) {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  };
}

function revokeBlob(url) {
  if (url && String(url).startsWith('blob:')) URL.revokeObjectURL(url);
}

function TipHost({ text, children }) {
  return (
    <span className="pcs2-tooltip-host cust-emp-tip-host" data-tooltip={text}>
      {children}
    </span>
  );
}

export default function CustomersEmpresasModal({ open, onClose, onToastSuccess, onToastError }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingName, setSavingName] = useState(null);
  const [uploadingCodigo, setUploadingCodigo] = useState(null);
  const [deletingCodigo, setDeletingCodigo] = useState(null);
  const [draftNames, setDraftNames] = useState({});
  const [previewUrls, setPreviewUrls] = useState({});
  const [expanded, setExpanded] = useState({});
  const fileRefs = useRef({});
  const nameBaseline = useRef({});
  const nameInputRefs = useRef({});

  const togglePanel = useCallback((codigo, panel) => {
    setExpanded((prev) => {
      const current = prev[codigo];
      if (current === panel) {
        const next = { ...prev };
        delete next[codigo];
        return next;
      }
      return { ...prev, [codigo]: panel };
    });
  }, []);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(API_BASE(), { headers: authHeaders() });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      const list = json.data?.companies || [];
      setCompanies(list);
      const names = {};
      const baselines = {};
      const previews = {};
      list.forEach((c) => {
        names[c.codigo] = c.displayName || '';
        baselines[c.codigo] = c.displayName || '';
        if (c.logo?.viewUrl) {
          previews[c.codigo] = c.logo.viewUrl;
        }
      });
      nameBaseline.current = baselines;
      setDraftNames(names);
      setPreviewUrls((prev) => {
        Object.values(prev).forEach(revokeBlob);
        return previews;
      });
    } catch (e) {
      setError(e.message || 'Erro ao carregar empresas');
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setExpanded({});
      loadCompanies();
    }
  }, [open, loadCompanies]);

  useEffect(() => {
    Object.entries(expanded).forEach(([codigo, panel]) => {
      if (panel === 'name') {
        const el = nameInputRefs.current[codigo];
        if (el) {
          requestAnimationFrame(() => el.focus());
        }
      }
    });
  }, [expanded]);

  useEffect(
    () => () => {
      Object.values(previewUrls).forEach(revokeBlob);
    },
    [previewUrls]
  );

  const saveDisplayName = async (codigo) => {
    const next = String(draftNames[codigo] ?? '').trim();
    if (next === (nameBaseline.current[codigo] || '')) return;
    setSavingName(codigo);
    try {
      const res = await fetch(`${API_BASE()}/${encodeURIComponent(codigo)}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ display_name: next }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || 'Falha ao salvar');
      nameBaseline.current[codigo] = json.data?.displayName || next;
      setDraftNames((prev) => ({ ...prev, [codigo]: nameBaseline.current[codigo] }));
      setCompanies((prev) =>
        prev.map((c) =>
          c.codigo === codigo ? { ...c, displayName: nameBaseline.current[codigo] } : c
        )
      );
      onToastSuccess?.('Nome de exibição salvo');
    } catch (e) {
      onToastError?.(e.message || 'Erro ao salvar nome');
      setDraftNames((prev) => ({ ...prev, [codigo]: nameBaseline.current[codigo] || '' }));
    } finally {
      setSavingName(null);
    }
  };

  const handleFileSelect = async (codigo, event) => {
    const input = event.currentTarget;
    const file = input?.files?.[0];
    if (!file || !(file instanceof Blob)) return;

    const previousPreview = previewUrls[codigo];
    setUploadingCodigo(codigo);
    let immediatePreview = null;
    try {
      const uploadFile = await compressCompanyLogo(file);
      immediatePreview = URL.createObjectURL(uploadFile);
      setPreviewUrls((prev) => {
        revokeBlob(prev[codigo]);
        return { ...prev, [codigo]: immediatePreview };
      });

      const presignRes = await fetch(`${API_BASE()}/${encodeURIComponent(codigo)}/logo/presign`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          mimeType: uploadFile.type,
          sizeBytes: uploadFile.size,
          fileName: uploadFile.name,
        }),
      });
      const presignData = await presignRes.json();
      if (!presignRes.ok || !presignData.success) {
        throw new Error(presignData.message || 'Falha ao preparar upload');
      }

      const uploadRes = await fetch(presignData.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': uploadFile.type },
        body: uploadFile,
      });
      if (!uploadRes.ok) throw new Error('Falha ao enviar imagem para o servidor');

      const completeRes = await fetch(`${API_BASE()}/${encodeURIComponent(codigo)}/logo/complete`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ s3Key: presignData.data.key }),
      });
      const completeData = await completeRes.json();
      if (!completeRes.ok || !completeData.success) {
        throw new Error(completeData.message || 'Falha ao confirmar upload');
      }

      revokeBlob(immediatePreview);
      const viewUrl = completeData.data?.logo?.viewUrl || null;
      setPreviewUrls((prev) => ({ ...prev, [codigo]: viewUrl }));
      setCompanies((prev) =>
        prev.map((c) =>
          c.codigo === codigo ? { ...c, logo: completeData.data?.logo || c.logo } : c
        )
      );
      onToastSuccess?.('Logo atualizada');
    } catch (err) {
      if (immediatePreview) revokeBlob(immediatePreview);
      setPreviewUrls((prev) => ({ ...prev, [codigo]: previousPreview }));
      onToastError?.(err.message || 'Erro ao enviar logo');
    } finally {
      setUploadingCodigo(null);
      input.value = '';
    }
  };

  const handleDeleteLogo = async (codigo) => {
    setDeletingCodigo(codigo);
    const previousPreview = previewUrls[codigo];
    try {
      const res = await fetch(`${API_BASE()}/${encodeURIComponent(codigo)}/logo`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || 'Falha ao excluir');
      revokeBlob(previousPreview);
      setPreviewUrls((prev) => {
        const next = { ...prev };
        delete next[codigo];
        return next;
      });
      setCompanies((prev) => prev.map((c) => (c.codigo === codigo ? { ...c, logo: null } : c)));
      onToastSuccess?.('Logo removida');
    } catch (err) {
      onToastError?.(err.message || 'Erro ao excluir logo');
    } finally {
      setDeletingCodigo(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="modal-overlay cx-email-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="cx-email-modal cx-email-send-modal cust-emp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cust-emp-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cx-email-send-header">
          <h2 id="cust-emp-modal-title" className="cx-email-send-title">
            Empresas
          </h2>
          <button type="button" className="cx-email-send-close" onClick={onClose} aria-label="Fechar">
            <X size={18} strokeWidth={2} />
          </button>
        </header>

        <div className="cx-email-modal-body">
          <div className="cx-email-send-scroll">
            <p className="cx-email-send-intro">
              Defina o nome visível no login do portal e a logomarca de cada empresa. Com logo, o
              portal exibe a imagem; sem logo, usa o nome de exibição.
            </p>

            {error ? (
              <div className="cx-email-send-result cx-email-send-result--err" role="alert">
                {error}
                <button type="button" className="cx-email-btn cx-email-btn--ghost" onClick={loadCompanies}>
                  Tentar novamente
                </button>
              </div>
            ) : null}

            {loading ? (
              <div className="cx-email-send-loading">
                <Loader2 size={22} className="cx-exp-spin" aria-hidden />
                Carregando empresas…
              </div>
            ) : companies.length === 0 ? (
              <div className="cx-email-send-empty">
                <Building2 size={24} strokeWidth={1.5} aria-hidden />
                Nenhuma empresa ativa encontrada.
              </div>
            ) : (
              <ul className="cust-emp-list">
                {companies.map((emp) => {
                  const preview = previewUrls[emp.codigo] || null;
                  const hasLogo = Boolean(preview || emp.logo?.hasLogo || emp.logo?.s3Key);
                  const busy =
                    uploadingCodigo === emp.codigo ||
                    deletingCodigo === emp.codigo ||
                    savingName === emp.codigo;
                  const openPanel = expanded[emp.codigo] || null;
                  const nameOpen = openPanel === 'name';
                  const logoOpen = openPanel === 'logo';
                  const displayHint = (draftNames[emp.codigo] || '').trim();

                  return (
                    <li
                      key={emp.codigo}
                      className={`cust-emp-card${openPanel ? ' cust-emp-card--expanded' : ''}`}
                    >
                      <div className="cust-emp-card-head">
                        <span className="cust-emp-codigo">{emp.codigo}</span>
                        <div className="cust-emp-meta">
                          <span className="cust-emp-nome" title={emp.nome}>
                            {emp.nome}
                          </span>
                          {emp.cnpj ? <span className="cust-emp-cnpj">{emp.cnpj}</span> : null}
                        </div>
                        <div className="cust-emp-head-actions">
                          <CxExpAnchorTooltip
                            text={
                              displayHint
                                ? `Nome visível: ${displayHint}`
                                : 'Definir nome de exibição'
                            }
                            align="end"
                            compact
                          >
                            <button
                              type="button"
                              className={`cust-emp-icon-btn${nameOpen ? ' is-active' : ''}${
                                displayHint ? ' has-value' : ''
                              }`}
                              disabled={busy}
                              aria-label="Nome de exibição"
                              aria-expanded={nameOpen}
                              onClick={() => togglePanel(emp.codigo, 'name')}
                            >
                              <Type size={14} strokeWidth={2} aria-hidden />
                            </button>
                          </CxExpAnchorTooltip>
                          <CxExpAnchorTooltip
                            text={hasLogo ? 'Alterar logo' : 'Definir logo'}
                            align="end"
                            compact
                          >
                            <button
                              type="button"
                              className={`cust-emp-icon-btn cust-emp-icon-btn--logo${
                                logoOpen ? ' is-active' : ''
                              }${hasLogo ? ' has-thumb' : ''}`}
                              disabled={busy}
                              aria-label="Logo da empresa"
                              aria-expanded={logoOpen}
                              onClick={() => togglePanel(emp.codigo, 'logo')}
                            >
                              {uploadingCodigo === emp.codigo ? (
                                <Loader2 size={14} className="cx-exp-spin" aria-hidden />
                              ) : hasLogo ? (
                                <img src={preview} alt="" className="cust-emp-head-thumb" />
                              ) : (
                                <ImageUp size={14} strokeWidth={2} aria-hidden />
                              )}
                            </button>
                          </CxExpAnchorTooltip>
                        </div>
                      </div>

                      {nameOpen ? (
                        <div className="cust-emp-panel cust-emp-field">
                          <div className="cust-emp-label-row">
                            <label className="cust-emp-label" htmlFor={`cust-emp-name-${emp.codigo}`}>
                              Nome visível
                            </label>
                            <TipHost text="Como o cliente verá o nome da empresa ao logar no portal">
                              <Ci2HelpHexIcon label="Como o cliente verá o nome da empresa ao logar no portal" />
                            </TipHost>
                          </div>
                          <div className="cust-emp-name-wrap">
                            <input
                              id={`cust-emp-name-${emp.codigo}`}
                              ref={(el) => {
                                nameInputRefs.current[emp.codigo] = el;
                              }}
                              type="text"
                              className="cust-emp-name-input"
                              maxLength={120}
                              value={draftNames[emp.codigo] ?? ''}
                              disabled={busy}
                              placeholder={emp.nome || 'Nome no portal'}
                              onChange={(e) =>
                                setDraftNames((prev) => ({
                                  ...prev,
                                  [emp.codigo]: e.target.value,
                                }))
                              }
                              onBlur={() => saveDisplayName(emp.codigo)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  e.currentTarget.blur();
                                }
                              }}
                            />
                            {savingName === emp.codigo ? (
                              <Loader2
                                size={14}
                                className="cx-exp-spin cust-emp-name-spin"
                                aria-hidden
                              />
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      {logoOpen ? (
                        <div className="cust-emp-panel cust-emp-field">
                          <div className="cust-emp-label-row">
                            <span className="cust-emp-label">Logo</span>
                            <TipHost text="Tamanho recomendado: 192 × 192 pixels · Proporção: 1:1">
                              <Ci2HelpHexIcon label="Tamanho recomendado: 192 × 192 pixels. Proporção: 1:1" />
                            </TipHost>
                          </div>
                          <div className="cust-emp-logo-row">
                            <div
                              className="cust-emp-logo-preview"
                              aria-hidden={hasLogo ? undefined : true}
                            >
                              {hasLogo ? (
                                <img src={preview} alt="" className="cust-emp-logo-preview-img" />
                              ) : (
                                <ImageIcon size={18} strokeWidth={1.75} aria-hidden />
                              )}
                            </div>
                            <input
                              ref={(el) => {
                                fileRefs.current[emp.codigo] = el;
                              }}
                              type="file"
                              accept={COMPANY_LOGO_ACCEPT}
                              className="cust-emp-logo-input-hidden"
                              onChange={(e) => handleFileSelect(emp.codigo, e)}
                            />
                            <button
                              type="button"
                              className="user-form-link-btn company-config-logo-update-btn"
                              disabled={busy}
                              onClick={() => fileRefs.current[emp.codigo]?.click()}
                            >
                              {uploadingCodigo === emp.codigo ? 'Enviando...' : 'Atualizar logo'}
                            </button>
                            {hasLogo ? (
                              <button
                                type="button"
                                className="user-form-link-btn cust-emp-logo-remove"
                                disabled={busy}
                                onClick={() => handleDeleteLogo(emp.codigo)}
                              >
                                {deletingCodigo === emp.codigo ? 'Removendo...' : 'Remover'}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <footer className="cx-email-modal-footer">
          <span aria-hidden="true" />
          <div className="cx-email-modal-footer-actions">
            <button
              type="button"
              className="cx-email-btn cx-email-btn--primary"
              onClick={onClose}
            >
              Fechar
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
}
